import * as React from "react"
import { cn } from "@/lib/utils"

export interface TabItem {
  value: string
  label: string
  disabled?: boolean
}

export interface TabsProps extends React.HTMLAttributes<HTMLDivElement> {
  items: TabItem[]
  value?: string
  defaultValue?: string
  onValueChange?: (value: string) => void
  variant?: "default" | "small"
}

const Tabs = React.forwardRef<HTMLDivElement, TabsProps>(
  (
    { className, items, value, defaultValue, onValueChange, variant = "default", ...props },
    ref
  ) => {
    const [selectedValue, setSelectedValue] = React.useState(
      value || defaultValue || items[0]?.value
    )

    React.useEffect(() => {
      if (value !== undefined) {
        setSelectedValue(value)
      }
    }, [value])

    const handleSelect = (itemValue: string) => {
      if (!value) {
        setSelectedValue(itemValue)
      }
      onValueChange?.(itemValue)
    }

    if (variant === "small") {
      return (
        <div
          ref={ref}
          className={cn(
            "inline-flex items-center justify-center p-1 h-9 rounded-[10px] bg-[#ebf0f6]",
            className
          )}
          {...props}
        >
          {items.map((item) => (
            <TabSmall
              key={item.value}
              active={selectedValue === item.value}
              disabled={item.disabled}
              onClick={() => !item.disabled && handleSelect(item.value)}
            >
              {item.label}
            </TabSmall>
          ))}
        </div>
      )
    }

    return (
      <div
        ref={ref}
        className={cn("flex items-center shadow-[0px_2px_8px_0px_rgba(19,46,90,0.05)]", className)}
        {...props}
      >
        {items.map((item) => (
          <Tab
            key={item.value}
            active={selectedValue === item.value}
            disabled={item.disabled}
            onClick={() => !item.disabled && handleSelect(item.value)}
          >
            {item.label}
          </Tab>
        ))}
      </div>
    )
  }
)
Tabs.displayName = "Tabs"

interface TabProps {
  active?: boolean
  disabled?: boolean
  onClick?: () => void
  children: React.ReactNode
}

const Tab: React.FC<TabProps> = ({ active, disabled, onClick, children }) => {
  return (
    <button
      type="button"
      className={cn(
        "w-full py-1.5 text-xs font-semibold text-center whitespace-nowrap",
        "transition-colors",
        active ? "bg-[#354267] text-white" : "bg-white text-[#3f4652] border-b border-[#ebf0f6]",
        disabled && "opacity-50 cursor-not-allowed",
        !disabled && "cursor-pointer hover:opacity-80"
      )}
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </button>
  )
}

interface TabSmallProps {
  active?: boolean
  disabled?: boolean
  onClick?: () => void
  children: React.ReactNode
}

const TabSmall: React.FC<TabSmallProps> = ({ active, disabled, onClick, children }) => {
  return (
    <button
      type="button"
      className={cn(
        "px-3 h-[26px] text-sm font-semibold text-[#354267] whitespace-nowrap rounded-[7px]",
        "transition-all",
        active && "bg-white shadow-[0px_3px_3px_0px_rgba(0,0,0,0.05)]",
        disabled && "opacity-50 cursor-not-allowed",
        !disabled && "cursor-pointer hover:opacity-80"
      )}
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </button>
  )
}

export { Tabs }
